import React, { useMemo } from 'react';
import { usePostHogQuery } from '../../hooks/usePostHogQuery';
import { buildTrendsQuery } from '../../utils/posthogApi';
import KpiCard from './components/KpiCard';
import TrendChart from './components/TrendChart';
import PlatformUsageChart from './components/PlatformUsageChart';
import { formatDate } from './utils';

/**
 * Extract total count from a TrendsQuery result for a given series index.
 * Uses aggregated_value if present, otherwise falls back to count.
 */
function extractTotal(results, seriesIndex = 0) {
  const series = results?.results?.[seriesIndex];
  if (!series) return 0;
  return series.aggregated_value ?? series.count ?? 0;
}

/**
 * Extract yesterday's value from a daily TrendsQuery result.
 * Used for DAU/WAU/MAU where "count" is the sum of all days (misleading)
 * and we want a single day's snapshot. Uses second-to-last value since
 * today's data is incomplete.
 */
function extractYesterday(results, seriesIndex = 0) {
  const series = results?.results?.[seriesIndex];
  if (!series?.data?.length) return 0;
  const data = series.data;
  // Use second-to-last day (yesterday) since today is incomplete
  return data.length >= 2 ? data[data.length - 2] : data[data.length - 1];
}

/**
 * Extract daily data points from a TrendsQuery result for sparklines.
 */
function extractDailyData(results, seriesIndex = 0) {
  const series = results?.results?.[seriesIndex];
  if (!series) return [];
  return (series.labels || []).map((label, i) => ({
    date: label,
    value: series.data?.[i] ?? 0,
  }));
}

const PERIOD_LABELS = {
  '-7d': 'vs prior 7 days',
  '-14d': 'vs prior 14 days',
  '-30d': 'vs prior 30 days',
  '-90d': 'vs prior 90 days',
};

const PulseCheckView = ({ dateRange }) => {
  const dateFrom = dateRange || '-30d';

  // Previous period for comparison
  const daysMap = { '-7d': 7, '-14d': 14, '-30d': 30, '-90d': 90 };
  const days = daysMap[dateFrom] || 30;
  const prevDateFrom = `-${days * 2}d`;
  const prevDateTo = `-${days}d`;
  const changeLabel = PERIOD_LABELS[dateFrom] || `vs prior ${days} days`;

  // ── KPI Queries ──

  // DAU (current period)
  const { data: dauData, loading: dauLoading } = usePostHogQuery(
    () => buildTrendsQuery([{ event: '$pageview', math: 'dau' }], dateFrom),
    { deps: [dateFrom] }
  );

  // DAU (previous period)
  const { data: dauPrevData } = usePostHogQuery(
    () => ({
      query: {
        kind: 'TrendsQuery',
        series: [{ kind: 'EventsNode', event: '$pageview', math: 'dau' }],
        dateRange: { date_from: prevDateFrom, date_to: prevDateTo },
        interval: 'day',
      },
    }),
    { deps: [prevDateFrom, prevDateTo] }
  );

  // WAU (current period)
  const { data: wauData, loading: wauLoading } = usePostHogQuery(
    () => buildTrendsQuery([{ event: '$pageview', math: 'weekly_active' }], dateFrom),
    { deps: [dateFrom] }
  );

  // WAU (previous period)
  const { data: wauPrevData } = usePostHogQuery(
    () => ({
      query: {
        kind: 'TrendsQuery',
        series: [{ kind: 'EventsNode', event: '$pageview', math: 'weekly_active' }],
        dateRange: { date_from: prevDateFrom, date_to: prevDateTo },
        interval: 'day',
      },
    }),
    { deps: [prevDateFrom, prevDateTo] }
  );

  // MAU (current period)
  const { data: mauData, loading: mauLoading } = usePostHogQuery(
    () => buildTrendsQuery([{ event: '$pageview', math: 'monthly_active' }], dateFrom),
    { deps: [dateFrom] }
  );

  // MAU (previous period)
  const { data: mauPrevData } = usePostHogQuery(
    () => ({
      query: {
        kind: 'TrendsQuery',
        series: [{ kind: 'EventsNode', event: '$pageview', math: 'monthly_active' }],
        dateRange: { date_from: prevDateFrom, date_to: prevDateTo },
        interval: 'day',
      },
    }),
    { deps: [prevDateFrom, prevDateTo] }
  );

  // Signups this period
  const { data: signupsData, loading: signupsLoading } = usePostHogQuery(
    () => buildTrendsQuery(['signup_completed'], dateFrom),
    { deps: [dateFrom] }
  );

  // Signups previous period
  const { data: signupsPrevData } = usePostHogQuery(
    () => ({
      query: {
        kind: 'TrendsQuery',
        series: [{ kind: 'EventsNode', event: 'signup_completed', math: 'total' }],
        dateRange: { date_from: prevDateFrom, date_to: prevDateTo },
      },
    }),
    { deps: [prevDateFrom, prevDateTo] }
  );

  // Error count (last 24h — fixed regardless of date range)
  const { data: errorsData, loading: errorsLoading } = usePostHogQuery(
    () => buildTrendsQuery(['$exception'], '-1d'),
    { deps: [] }
  );

  // ── Platform Usage (HogQL) ──

  const { data: platformRaw, loading: platformLoading } = usePostHogQuery(
    () => ({
      query: {
        kind: 'HogQLQuery',
        query: `SELECT
          toDate(timestamp) as day,
          countDistinctIf(person_id, properties.$lib = 'web') as web,
          countDistinctIf(person_id, properties.$lib = 'posthog-flutter' AND properties.$os = 'iOS') as ios,
          countDistinctIf(person_id, properties.$lib = 'posthog-flutter' AND properties.$os = 'Android') as android
        FROM events
        WHERE timestamp > now() - interval ${days} day
          AND person_id NOT IN (SELECT person_id FROM raw_cohort_people WHERE cohort_id = 220647)
        GROUP BY day
        ORDER BY day ASC`,
      },
    }),
    { deps: [days] }
  );

  const platformData = useMemo(() => {
    if (!platformRaw?.results?.length) return [];
    return platformRaw.results.map((row) => ({
      date: formatDate(String(row[0])),
      web: row[1] ?? 0,
      ios: row[2] ?? 0,
      android: row[3] ?? 0,
    }));
  }, [platformRaw]);

  // ── Sparkline Queries ──

  const sparklineEvents = [
    { event: 'login_completed', math: 'total' },
    { event: 'signup_completed', math: 'total' },
    { event: '$pageview', math: 'total' },
    { event: 'artwork_uploaded', math: 'total' },
    { event: 'Application Opened', math: 'total' },
  ];

  const { data: sparkData, loading: sparkLoading } = usePostHogQuery(
    () => buildTrendsQuery(sparklineEvents, dateFrom),
    { deps: [dateFrom] }
  );

  // Build chart data from spark results
  const sparkChartData = (() => {
    if (!sparkData?.results?.length) return [];
    const labels = sparkData.results[0]?.labels || [];
    return labels.map((label, i) => ({
      date: formatDate(label),
      logins: sparkData.results[0]?.data?.[i] ?? 0,
      signups: sparkData.results[1]?.data?.[i] ?? 0,
      pageviews: sparkData.results[2]?.data?.[i] ?? 0,
      uploads: sparkData.results[3]?.data?.[i] ?? 0,
      appOpens: sparkData.results[4]?.data?.[i] ?? 0,
    }));
  })();

  return (
    <div className="bi-pulse-check">
      {/* KPI Cards */}
      <div className="bi-kpi-grid">
        <KpiCard
          title="Daily Active Users"
          info="Unique users who visited at least one page yesterday. Includes users with existing sessions — not just new logins."
          value={extractYesterday(dauData)}
          previousValue={extractYesterday(dauPrevData)}
          changeLabel={changeLabel}
          loading={dauLoading}
        />
        <KpiCard
          title="Weekly Active Users"
          info="Unique users who visited at least one page in the last 7 days. A single user is counted once regardless of how many days they were active."
          value={extractYesterday(wauData)}
          previousValue={extractYesterday(wauPrevData)}
          changeLabel={changeLabel}
          loading={wauLoading}
        />
        <KpiCard
          title="Monthly Active Users"
          info="Unique users who visited at least one page in the last 30 days. This is the broadest measure of your active user base."
          value={extractYesterday(mauData)}
          previousValue={extractYesterday(mauPrevData)}
          changeLabel={changeLabel}
          loading={mauLoading}
        />
        <KpiCard
          title="Signups"
          info="Total number of completed signups during this period. Counts every signup event, not unique users."
          value={extractTotal(signupsData)}
          previousValue={extractTotal(signupsPrevData)}
          sparklineData={extractDailyData(signupsData)}
          changeLabel={changeLabel}
          loading={signupsLoading}
        />
        <KpiCard
          title="Errors (24h)"
          info="Total exceptions captured in the last 24 hours across web and mobile apps."
          value={extractTotal(errorsData)}
          loading={errorsLoading}
        />
      </div>

      {/* Platform Usage */}
      <PlatformUsageChart data={platformData} loading={platformLoading} />

      {/* Trend Charts */}
      <div className="bi-charts-grid">
        <TrendChart
          title="Logins & Signups"
          data={sparkChartData}
          lines={[
            { key: 'logins', label: 'Logins', color: '#6366f1' },
            { key: 'signups', label: 'Signups', color: '#10b981' },
          ]}
          loading={sparkLoading}
        />
        <TrendChart
          title="Pageviews"
          data={sparkChartData}
          lines={[{ key: 'pageviews', label: 'Pageviews', color: '#3b82f6' }]}
          loading={sparkLoading}
        />
        <TrendChart
          title="Art Uploads"
          data={sparkChartData}
          lines={[{ key: 'uploads', label: 'Uploads', color: '#8b5cf6' }]}
          loading={sparkLoading}
        />
        <TrendChart
          title="App Opens"
          data={sparkChartData}
          lines={[{ key: 'appOpens', label: 'App Opens', color: '#f59e0b' }]}
          loading={sparkLoading}
        />
      </div>
    </div>
  );
};

export default PulseCheckView;
